import React from 'react';
import { Box, Stack, CircularProgress, Typography } from '@mui/material';
import { useSummaryFetch, WidgetLoadError } from './useSummaryFetch';
import { PERMISSIONS } from '../../../constants/permissions';
import { DashboardService } from '../../../services/dashboard.service';
import { AccountingSummary, MonthlyIncomeExpense, ArAgingBucket, AccountAmount, TopReceivable } from '../../../interfaces/dashboard.interface';
import { WidgetDef, WidgetProps } from './types';
import StatTile from '../charts/StatTile';
import Columns from '../charts/Columns';
import { compactMoney, formatPeriodLabel } from '../charts/axisPadding';
import HBars from '../charts/HBars';
import { fmtMoney, CURRENCY_SYMBOL, previousMonthEntry } from './format';
import { FpaService, KpiResult } from '../../../services/fpa.service'; // W3-V
import { WithholdingService } from '../../../services/withholding.service'; // B-24

const DASHBOARD_PERMISSION = PERMISSIONS.DASHBOARD_VIEW;

const loadAccounting = (institutionId: string): Promise<AccountingSummary> =>
    DashboardService.getAccountingSummary(institutionId);

const AccountingWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadAccounting, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    // A real zero (e.g. every invoice paid, every bill settled, cash net to 0.00) is a genuine
    // figure, not an absence of data — render it as a zero rather than collapsing it into an
    // "empty state" claim that isn't true.
    return (
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {/* The tile is the unpaid Sales-invoice sub-ledger (ties to the aging widgets below). The ledger
                figure is the whole Receivables group as the balance sheet shows it: export and journal-posted
                receivables, but also employee advances, deposits and intercompany balances, net of bad-debt
                provision. When it is larger the tile says so instead of silently disagreeing. */}
            <StatTile
                label="Receivable"
                unit={CURRENCY_SYMBOL}
                value={fmtMoney(d.arOutstanding)}
                sub={d.arLedgerBalance != null ? `Invoices · all receivables ledger ${CURRENCY_SYMBOL}${fmtMoney(d.arLedgerBalance)}` : undefined}
                tone={d.arOutstanding > 0 ? 'warning' : 'default'}
            />
            <StatTile label="Payable" unit={CURRENCY_SYMBOL} value={fmtMoney(d.apOutstanding)} tone={d.apOutstanding > 0 ? 'warning' : 'default'} />
            {/* Cash is only "good" when positive: a zero is neutral and an overdrawn/net-credit
                balance must never render success-green. */}
            <StatTile
                label="Cash & bank"
                unit={CURRENCY_SYMBOL}
                value={fmtMoney(d.cashBalance)}
                tone={d.cashBalance > 0 ? 'good' : d.cashBalance < 0 ? 'critical' : 'default'}
            />
        </Stack>
    );
};

const loadAccountingTrend = (institutionId: string): Promise<MonthlyIncomeExpense[]> =>
    DashboardService.getAccountingTrend(institutionId);

const AccountingTrendWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadAccountingTrend, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No posted journal activity yet — post journal entries to see income and expense here.</Typography>;
    }
    // A month where nothing was posted is a genuine zero: it stays on the axis as a zero column.
    // Both charts share one scale (the larger of the two series' maxima) so income and expense
    // are visually comparable — a real-zero month (both series flat 0) falls back to the default
    // auto-scale via Columns' own Math.max(1, ...) guard.
    const sharedMax = Math.max(...d.map(p => p.income), ...d.map(p => p.expense));
    return (
        // Side by side on wide screens (stacked on phones) so the pair reads as one comparison
        // instead of two tall, mostly empty charts.
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(2, minmax(0,1fr))' }, gap: 2 }}>
            <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">Income</Typography>
                <Columns data={d.map(p => ({ label: p.month, value: p.income }))} yFmt={fmtMoney} tickFmt={compactMoney} colorIndex={0} height={160} max={sharedMax} />
            </Stack>
            <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">Expense</Typography>
                <Columns data={d.map(p => ({ label: p.month, value: p.expense }))} yFmt={fmtMoney} tickFmt={compactMoney} colorIndex={1} height={160} max={sharedMax} />
            </Stack>
        </Box>
    );
};

// Previous calendar month's posted income / expense / net — the "this month" tiles read zero on the
// 1st, so this keeps the month that just closed visible. Same trend endpoint (caller's zone).
const AccountingLastMonthWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadAccountingTrend, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    const prev = previousMonthEntry(d);
    if (!prev) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No previous month to show yet.</Typography>;
    }
    const net = prev.income - prev.expense;
    return (
        <Stack spacing={0.5}>
            <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <StatTile label="Income · last month" unit={CURRENCY_SYMBOL} value={fmtMoney(prev.income)} />
                <StatTile label="Expense · last month" unit={CURRENCY_SYMBOL} value={fmtMoney(prev.expense)} />
                <StatTile label="Net · last month" unit={CURRENCY_SYMBOL} value={fmtMoney(net)} tone={net > 0 ? 'good' : net < 0 ? 'critical' : 'default'} />
            </Stack>
            <Typography variant="caption" color="text.secondary">{formatPeriodLabel(prev.month)} · posted journals</Typography>
        </Stack>
    );
};

const loadArAging = (institutionId: string): Promise<ArAgingBucket[]> =>
    DashboardService.getAccountingArAging(institutionId);

const AccountingArAgingWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadArAging, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    // The backend zero-fills all five buckets while any invoice is open, so an empty array
    // really does mean "no open invoices" — a zero-filled bucket set renders as zero bars.
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No open invoices — nothing is aging.</Typography>;
    }
    return (
        <Stack spacing={0.5}>
            <HBars
                data={d.map(b => ({
                    label: b.bucket,
                    value: b.amount,
                    tooltip: [
                        { label: 'Outstanding', value: fmtMoney(b.amount) },
                        { label: 'Invoices', value: String(b.invoiceCount) },
                    ],
                }))}
                vFmt={fmtMoney}
            />
            <Typography variant="caption" color="text.secondary">
                Sales invoices only · invoices without a due date age from issue + 30 days
            </Typography>
        </Stack>
    );
};

const loadExpenseByAccount = (institutionId: string): Promise<AccountAmount[]> =>
    DashboardService.getAccountingExpenseByAccount(institutionId);

const AccountingExpenseByAccountWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadExpenseByAccount, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No expense postings yet — post journal entries to see spend by account.</Typography>;
    }
    return (
        <Stack spacing={0.5}>
            <HBars
                data={d.map(a => ({
                    label: a.accountName,
                    value: a.amount,
                    tooltip: [{ label: 'Spend', value: fmtMoney(a.amount) }],
                }))}
                vFmt={fmtMoney}
            />
            <Typography variant="caption" color="text.secondary">This month · net of reclassifications</Typography>
        </Stack>
    );
};

const loadTopReceivables = (institutionId: string): Promise<TopReceivable[]> =>
    DashboardService.getAccountingTopReceivables(institutionId);

const AccountingTopReceivablesWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadTopReceivables, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No outstanding invoices — every customer is settled.</Typography>;
    }
    return (
        <HBars
            data={d.map(r => ({
                label: r.customerName,
                value: r.outstanding,
                tooltip: [
                    { label: 'Outstanding', value: fmtMoney(r.outstanding) },
                    { label: 'Invoices', value: String(r.invoiceCount) },
                ],
            }))}
            vFmt={fmtMoney}
        />
    );
};

// ---- W3-V KPI strip ----
const KPI_STRIP_KEYS = ['currentRatio', 'dso', 'dpo', 'ccc', 'grossMargin'];

const loadKpiStrip = (): Promise<KpiResult> => FpaService.getKpis();

const AccountingKpiStripWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadKpiStrip, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    const shown = KPI_STRIP_KEYS.map(k => d.kpis.find(x => x.key === k)).filter((k): k is NonNullable<typeof k> => !!k);
    return (
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {shown.map(k => (
                <StatTile key={k.key} label={k.label} value={k.value === null ? 'n/a' : k.value.toFixed(2)}
                    unit={k.unit === '%' ? '%' : k.unit === 'days' ? 'days' : undefined} sub={k.howComputed} />
            ))}
        </Stack>
    );
};
// ---- end W3-V ----

// ---- B-24 withheld, not deposited ----
const loadWithheldNotDeposited = (institutionId: string) => WithholdingService.getUndepositedSummary(institutionId);

const AccountingWithheldNotDepositedWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadWithheldNotDeposited, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    const certs = (n: number) => `${n} ${n === 1 ? 'certificate' : 'certificates'}`;
    const oldest = (date?: string | null) => (date ? ` · oldest paid ${date}` : '');
    return (
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <StatTile label="VDS to deposit" unit={CURRENCY_SYMBOL} value={fmtMoney(d.vdsAmount)}
                sub={`${certs(d.vdsCount)}${oldest(d.oldestVdsPaymentDate)}`} tone={d.vdsAmount > 0 ? 'warning' : 'default'} />
            <StatTile label="TDS to deposit" unit={CURRENCY_SYMBOL} value={fmtMoney(d.tdsAmount)}
                sub={`${certs(d.tdsCount)}${oldest(d.oldestTdsPaymentDate)}`} tone={d.tdsAmount > 0 ? 'warning' : 'default'} />
        </Stack>
    );
};
// ---- end B-24 ----

export const accountingWidgets: WidgetDef[] = [
    { id: 'accounting.summary', module: 'accounting', title: 'Accounting — AR, AP & cash', subtitle: 'Unpaid invoices, unpaid bills and cash on hand', permission: DASHBOARD_PERMISSION, size: 'md', component: AccountingWidget },
    { id: 'accounting.trend', module: 'accounting', title: 'Accounting — income vs expense', subtitle: 'Posted income and expense per month, last 6 months', permission: DASHBOARD_PERMISSION, size: 'lg', component: AccountingTrendWidget },
    { id: 'accounting.lastMonth', module: 'accounting', title: 'Accounting — last month', subtitle: 'Posted income, expense and net for the previous calendar month', permission: DASHBOARD_PERMISSION, size: 'md', component: AccountingLastMonthWidget },
    { id: 'accounting.arAging', module: 'accounting', title: 'Accounting — AR aging', subtitle: 'Unpaid sales invoices by days overdue', permission: DASHBOARD_PERMISSION, size: 'md', component: AccountingArAgingWidget },
    { id: 'accounting.expenseByAccount', module: 'accounting', title: 'Accounting — expense by account', subtitle: 'Where money went this month, by expense account', permission: DASHBOARD_PERMISSION, size: 'lg', component: AccountingExpenseByAccountWidget },
    { id: 'accounting.topReceivables', module: 'accounting', title: 'Accounting — top receivables', subtitle: 'Customers who owe you the most right now', permission: DASHBOARD_PERMISSION, size: 'md', component: AccountingTopReceivablesWidget },
    // W3-V (append-only; not in any default layout)
    { id: 'accounting.kpiStrip', module: 'accounting', title: 'Accounting — KPI strip', subtitle: 'Current ratio, DSO, DPO, cash conversion cycle and gross margin', permission: PERMISSIONS.FINANCIAL_REPORT_VIEW, size: 'lg', component: AccountingKpiStripWidget },
    // B-24 (append-only; not in any default layout)
    { id: 'accounting.withheldNotDeposited', module: 'accounting', title: 'Withheld, not deposited', subtitle: 'VDS and TDS kept back from suppliers and not yet paid to the treasury', permission: PERMISSIONS.SUPPLIER_PAYMENT_VIEW, size: 'md', component: AccountingWithheldNotDepositedWidget },
];
